import { ShoppingBag, CheckCircle, Clock, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Theme } from '@/shared/constants/themes';

interface OrderProduct {
    name: string;
    size: string;
    status: 'paid' | 'pending';
    price: string;
}

interface OrderSummary {
    id: string;
    customer: string;
    time: string;
    products: OrderProduct[];
}

interface LiveOrdersProps {
    orders?: OrderSummary[];
    theme?: Theme;
}

export const LiveOrders: React.FC<LiveOrdersProps> = ({ orders = [], theme }) => {
    const themeColors = theme?.colors;
    const [muted, setMuted] = useState<boolean>(() => {
        try {
            const v = localStorage.getItem('liveOrdersMuted');
            return v === 'true';
        } catch {
            return false;
        }
    });

    // Track IDs of orders that are already paid to detect new paid ones
    const prevPaidIdsRef = useRef<Set<string>>(new Set(
        orders
            .filter(o => o.products.some(p => p.status === 'paid'))
            .map(o => o.id)
    ));

    useEffect(() => {
        try {
            localStorage.setItem('liveOrdersMuted', muted ? 'true' : 'false');
        } catch { }
    }, [muted]);

    function playSuccessSound() {
        try {
            const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
            if (!AudioContext) return;

            const ctx = new AudioContext();
            const now = ctx.currentTime;

            // Magical Sparkle Sound ✨

            // Master gain (enveloppe douce)
            const master = ctx.createGain();
            master.gain.setValueAtTime(0.0001, now);
            master.gain.exponentialRampToValueAtTime(0.35, now + 0.12);
            master.gain.exponentialRampToValueAtTime(0.0001, now + 1.1);
            master.connect(ctx.destination);

            // Notes très aiguës = effet magique ✨
            const sparkles = [
                { freq: 1760, start: 0.00, dur: 0.35 }, // A6
                { freq: 2093, start: 0.08, dur: 0.30 }, // C7
                { freq: 2637, start: 0.16, dur: 0.40 }, // E7
                { freq: 3136, start: 0.28, dur: 0.45 }, // G7
            ];

            sparkles.forEach(s => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();

                osc.type = 'sine';
                osc.frequency.setValueAtTime(s.freq, now + s.start);

                gain.gain.setValueAtTime(0.0001, now + s.start);
                gain.gain.exponentialRampToValueAtTime(0.22, now + s.start + 0.05);
                gain.gain.exponentialRampToValueAtTime(0.0001, now + s.start + s.dur);

                osc.connect(gain);
                gain.connect(master);

                osc.start(now + s.start);
                osc.stop(now + s.start + s.dur + 0.05);
            });

            // Cleanup safely
            setTimeout(() => {
                try {
                    if (ctx.state !== 'closed') ctx.close();
                } catch { }
            }, 1500);

        } catch (e) {
            console.error('Sound error:', e);
        }
    }

    function parsePrice(value: string): number {
        const cleaned = (value || '').replace(/[^0-9.,]/g, '').replace(',', '.');
        const num = parseFloat(cleaned);
        return Number.isFinite(num) ? num : 0;
    }

    useEffect(() => {
        // Collect current Paid IDs
        const currentPaidIds = new Set(
            orders
                .filter(o => o.products.some(p => p.status === 'paid'))
                .map(o => o.id)
        );

        // Check if we have any *new* paid ID that wasn't in the previous set
        const hasNewPaid = [...currentPaidIds].some(id => !prevPaidIdsRef.current.has(id));

        if (hasNewPaid && !muted) {
            playSuccessSound();
        }

        prevPaidIdsRef.current = currentPaidIds;
    }, [orders, muted]);

    return (
        <div
            className="rounded-lg shadow-sm border overflow-hidden"
            style={{
                backgroundColor: themeColors?.surface || '#FFFFFF',
                borderColor: themeColors?.border || '#E5E7EB'
            }}
        >
            {/* Header */}
            <div
                className="px-4 py-3 border-b"
                style={{
                    background: `linear-gradient(to right, ${themeColors?.successLight || '#D1FAE5'}20, ${themeColors?.success || '#10B981'}15)`,
                    borderBottomColor: themeColors?.border || '#E5E7EB'
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ShoppingBag
                            size={16}
                            style={{ color: themeColors?.success || '#10B981' }}
                        />
                        <h3
                            className="text-sm font-semibold"
                            style={{ color: themeColors?.foreground || '#374151' }}
                        >
                            Commandes
                        </h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <span
                            className="text-xs font-medium px-2 py-0.5 rounded-full"
                            style={{
                                color: themeColors?.success || '#10B981',
                                backgroundColor: themeColors?.surface || '#FFFFFF'
                            }}
                        >
                            &lt;5min
                        </span>
                        <button
                            type="button"
                            onClick={() => setMuted(m => !m)}
                            className="inline-flex items-center justify-center rounded-md border px-2 py-1 transition-colors"
                            style={{
                                borderColor: themeColors?.border || '#D1D5DB',
                                backgroundColor: themeColors?.surface || '#FFFFFF',
                                color: themeColors?.foreground || '#374151'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = themeColors?.backgroundTertiary || '#F3F4F6';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = themeColors?.surface || '#FFFFFF';
                            }}
                            aria-label={muted ? 'Activer le son' : 'Couper le son'}
                        >
                            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Orders List */}
            <div className="p-3 space-y-2">
                {orders.length === 0 ? (
                    <p
                        className="text-xs italic text-center py-6"
                        style={{ color: themeColors?.foregroundSecondary || '#9CA3AF' }}
                    >
                        Aucune commande récente
                    </p>
                ) : (
                    orders.map((order) => (
                        <div
                            key={order.id}
                            className="rounded-lg p-2.5 border transition-all"
                            style={{
                                backgroundColor: themeColors?.backgroundSecondary || '#F9FAFB',
                                borderColor: themeColors?.border || '#E5E7EB'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = themeColors?.success || '#10B981';
                                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = themeColors?.border || '#E5E7EB';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            {/* Order Header */}
                            <div className="flex items-center justify-between mb-2">
                                <span
                                    className="text-xs font-semibold"
                                    style={{ color: themeColors?.foreground || '#111827' }}
                                >
                                    {order.customer}
                                </span>
                                <div className="flex items-center gap-2">
                                    <span
                                        className="text-[10px] flex items-center gap-1"
                                        style={{ color: themeColors?.foregroundSecondary || '#6B7280' }}
                                    >
                                        <Clock size={10} />
                                        {order.time}
                                    </span>
                                    <span
                                        className="text-[11px] font-semibold"
                                        style={{ color: themeColors?.foreground || '#111827' }}
                                    >
                                        {order.products.reduce((sum, p) => sum + parsePrice(p.price), 0).toFixed(2)}€
                                    </span>
                                </div>
                            </div>

                            {/* Products */}
                            <div className="space-y-1.5">
                                {order.products.map((product, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center justify-between border rounded-md p-2"
                                        style={{
                                            backgroundColor: themeColors?.surface || '#FFFFFF',
                                            borderColor: themeColors?.borderLight || '#F3F4F6'
                                        }}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div
                                                className="text-xs font-medium truncate"
                                                style={{ color: themeColors?.foreground || '#111827' }}
                                            >
                                                {product.name}
                                            </div>
                                            <div
                                                className="text-[10px]"
                                                style={{ color: themeColors?.foregroundSecondary || '#6B7280' }}
                                            >
                                                Taille {product.size} • {product.price}
                                            </div>
                                        </div>
                                        <div className="ml-2">
                                            {product.status === 'paid' ? (
                                                <div
                                                    className="flex items-center gap-1 px-1.5 py-0.5 rounded-full"
                                                    style={{
                                                        backgroundColor: themeColors?.successLight || '#D1FAE5',
                                                        color: themeColors?.successDark || '#065F46'
                                                    }}
                                                >
                                                    <CheckCircle size={10} />
                                                    <span className="text-[9px] font-semibold">Payé</span>
                                                </div>
                                            ) : (
                                                <div
                                                    className="flex items-center gap-1 px-1.5 py-0.5 rounded-full"
                                                    style={{
                                                        backgroundColor: themeColors?.warningLight || '#FEF3C7',
                                                        color: themeColors?.warningDark || '#92400E'
                                                    }}
                                                >
                                                    <Clock size={10} />
                                                    <span className="text-[9px] font-semibold">En cours</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
